import { useParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Header } from '@/components/landing/Header'
import { Footer } from '@/components/landing/Footer'
import { Link } from 'react-router-dom'
import { Breadcrumb } from '@/components/Breadcrumb'
import breadcrumbData from '@/data/breadcrumb-schema.json'
import { useEffect, useState } from 'react'
import { useTranslation } from '@/hooks/useTranslation'
import { canonicalizePath, toAbsoluteUrl } from '@/lib/seo'

type CategoryItem = {
  name: string;
  url: string;
  slug: string;
  description?: string;
};

type CategoryInfo = {
  title: string;
  description: string;
  items: CategoryItem[];
  noContent?: boolean;
};

const CategoryPage = () => {
  const { t } = useTranslation()
  // Utiliser le dernier segment du path pour déterminer si nous sommes sur features ou technologies
  const { categorySlug } = useParams<{ categorySlug?: string }>()
  const navigate = useNavigate();
  const path = window.location.pathname;
  const categoryType = path.includes('/categories/features') ? 'features' : 'technologies';
  const canonicalPath = canonicalizePath(window.location.pathname)

  // Lazy-load the pSEO pages JSON (15MB) — keeps it out of the main bundle.
  // We only need the slug keys here, not the page bodies.
  const [existingSlugs, setExistingSlugs] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    import('@/data/combinedPages2.json').then((mod) => {
      if (!cancelled) setExistingSlugs(Object.keys(mod.default));
    });
    return () => { cancelled = true; };
  }, []);

  // Générer les breadcrumbs
  const breadcrumbs = (() => {
    const crumbs = [{ name: "Home", url: "/" }];

    // Ajouter le breadcrumb correspondant au type de catégorie
    if (categoryType === 'features') {
      // Pour les fonctionnalités (ex: bug-report, feature-request)
      crumbs.push({
        name: "Features",
        url: "/categories/features"
      });

      // Si on a un slug spécifique, l'ajouter
      if (categorySlug) {
        const typedBreadcrumbData = breadcrumbData as any;
        const needInfo = typedBreadcrumbData.pageCategories.need[categorySlug];

        if (needInfo) {
          crumbs.push({
            name: needInfo.name,
            url: `/categories/features/${categorySlug}`
          });
        }
      }
    } else if (categoryType === 'technologies') {
      // Pour les technologies (ex: react, nextjs)
      crumbs.push({
        name: "Technologies",
        url: "/technologies"
      });

      // Si on a un slug spécifique, l'ajouter
      if (categorySlug) {
        const typedBreadcrumbData = breadcrumbData as any;
        const techInfo = typedBreadcrumbData.pageCategories.technology[categorySlug];

        if (techInfo) {
          crumbs.push({
            name: techInfo.name,
            url: `/technologies/${categorySlug}`
          });
        }
      }
    }

    return crumbs;
  })();

  // Obtenir les informations de la catégorie et les articles pertinents
  const categoryInfo = (() => {
    // Pour les pages de fonctionnalités (ex: bug-report, feature-request)
    if (categoryType === 'features' && categorySlug) {
      const typedBreadcrumbData = breadcrumbData as any;
      const needInfo = typedBreadcrumbData.pageCategories.need[categorySlug];
      if (!needInfo) return null;

      // Au lieu de filtrer les articles existants, générer des liens pour toutes les technologies
      const allTechnologies = Object.keys(typedBreadcrumbData.pageCategories.technology);

      // Générer des liens pour toutes les technologies disponibles
      const allTechLinks: CategoryItem[] = allTechnologies.map(techSlug => {
        const techInfo = typedBreadcrumbData.pageCategories.technology[techSlug];
        const slug = `${categorySlug}-saas-${techSlug}`;

        // Vérifier si l'article existe
        const articleExists = existingSlugs.includes(slug);

        return {
          name: `${needInfo.name} for ${techInfo.name || techSlug}`,
          url: `/feedback-widget/${slug}`,
          slug,
          description: `Learn how to implement ${needInfo.name.toLowerCase()} for your ${techInfo.name || techSlug} platform.`
        };
      });

      return {
        title: `${needInfo.name} Resources`,
        description: `Discover how to collect ${needInfo.name.toLowerCase()} across different platforms and technologies.`,
        items: allTechLinks,
        noContent: false
      } as CategoryInfo;
    }

    // Pour les pages de technologies (ex: react, nextjs)
    if (categoryType === 'technologies' && categorySlug) {
      const typedBreadcrumbData = breadcrumbData as any;
      const techInfo = typedBreadcrumbData.pageCategories.technology[categorySlug];
      if (!techInfo) return null;

      // Trouver tous les articles qui utilisent cette technologie
      const matchingArticles: CategoryItem[] = existingSlugs.filter(slug => {
        // Vérifier si le slug se termine par la technologie
        return slug.endsWith(`-${categorySlug}`);
      }).map(slug => {
        // Pour chaque article, extraire le type de besoin
        let needSlug = slug.split('-')[0];
        if (slug.startsWith('bug-report')) {
          needSlug = 'bug-report';
        } else if (slug.startsWith('feature-request')) {
          needSlug = 'feature-request';
        }

        const needInfo = typedBreadcrumbData.pageCategories.need[needSlug];

        return {
          name: `${needInfo?.name || needSlug} for ${techInfo.name}`,
          url: `/feedback-widget/${slug}`,
          slug,
          description: `Discover how to implement ${needInfo?.name?.toLowerCase() || needSlug} feedback for ${techInfo.name}.`
        };
      });

      // Même s'il n'y a pas d'articles, on renvoie une page plutôt que de rediriger
      return {
        title: `${techInfo.name} Resources`,
        description: `Find feedback collection solutions specifically for ${techInfo.name} implementations.`,
        items: matchingArticles.length > 0 ? matchingArticles : [],
        noContent: matchingArticles.length === 0
      } as CategoryInfo;
    }

    // Pour la page features principale
    if (categoryType === 'features' && !categorySlug) {
      const typedBreadcrumbData = breadcrumbData as any;

      // Liste de toutes les fonctionnalités qui ont des articles
      const featuresWithContent: CategoryItem[] = Object.keys(typedBreadcrumbData.pageCategories.need)
        .filter(need => {
          // Vérifier si ce besoin a des articles
          if (need === 'bug-report') {
            return existingSlugs.some(s => s.startsWith('bug-report-'));
          } else if (need === 'feature-request') {
            return existingSlugs.some(s => s.startsWith('feature-request-'));
          } else {
            return existingSlugs.some(s => s.startsWith(`${need}-`));
          }
        })
        .map(need => {
          const needInfo = typedBreadcrumbData.pageCategories.need[need];
          return {
            name: needInfo.name,
            url: `/categories/features/${need}`,
            slug: need,
            description: `Discover how to collect ${needInfo.name.toLowerCase()} on different platforms.`
          };
        });

      // Ajouter tous les besoins sans filtrer par existance d'articles pour tester
      const allFeatures: CategoryItem[] = Object.keys(typedBreadcrumbData.pageCategories.need)
        .map(need => {
          const needInfo = typedBreadcrumbData.pageCategories.need[need];
          return {
            name: needInfo.name,
            url: `/categories/features/${need}`,
            slug: need,
            description: `Discover how to collect ${needInfo.name.toLowerCase()} on different platforms.`
          };
        });


      // Utilisons toutes les features au lieu de filtrer
      return {
        title: "Feedback Features",
        description: "Explore different types of feedback you can collect with Feedbask.",
        items: allFeatures
      } as CategoryInfo;
    }

    // Pour la page technologies principale
    if (categoryType === 'technologies' && !categorySlug) {
      const typedBreadcrumbData = breadcrumbData as any;

      // Liste de toutes les technologies qui ont des articles
      const techsWithContent: CategoryItem[] = Object.keys(typedBreadcrumbData.pageCategories.technology)
        .filter(tech => {
          // Vérifier si cette technologie a des articles
          return existingSlugs.some(s => s.endsWith(`-${tech}`));
        })
        .map(tech => {
          const techInfo = typedBreadcrumbData.pageCategories.technology[tech];
          return {
            name: techInfo.name,
            url: `/technologies/${tech}`,
            slug: tech,
            description: `Feedback collection for ${techInfo.name} implementations.`
          };
        });

      return {
        title: "Technologies",
        description: "Find feedback solutions for different technology stacks and platforms.",
        items: techsWithContent
      } as CategoryInfo;
    }

    // Par défaut, rediriger vers la page d'accueil
    return null;
  })();

  // Rediriger si la catégorie n'existe pas ou n'a pas de contenu
  useEffect(() => {
    if (!categoryInfo) {
      navigate('/');
    }
  }, [categoryInfo, navigate]);

  if (!categoryInfo) {
    return null; // Sera redirigé par useEffect
  }

  return (
    <div className="min-h-screen w-full mx-auto relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>{categoryInfo.title} - Feedbask</title>
        <meta
          name="description"
          content={categoryInfo.description}
        />
        <link rel="canonical" href={toAbsoluteUrl(canonicalPath)} />
      </Helmet>

      {/* Background Blur */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10">
        <div className="absolute top-24 left-10 w-64 h-64 rounded-full bg-[#866BFF] opacity-30 blur-3xl"></div>
        <div className="absolute bottom-32 right-10 w-72 h-72 rounded-full bg-indigo-100 opacity-30 blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl px-4 mx-auto">
        <Header />

        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbs} />

        {/* Hero */}
        <section className="py-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {categoryInfo.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {categoryInfo.description}
          </p>
        </section>

        {/* Category Items List */}
        <section className="py-12">
          {categoryInfo.noContent ? (
            <div className="text-center p-8 bg-white rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('categoryPage.contentUnderCreation')}</h3>
              <p className="text-gray-600 mb-6">
                {t('categoryPage.activelyWorkingOnSection')}
              </p>
              <Link
                to="/categories/features"
                className="inline-block px-6 py-3 bg-[#866BFF] text-white rounded-lg hover:bg-[#7559e8] transition-colors"
              >
                {t('categoryPage.backToFeatures')}
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
              {categoryInfo.items.map((item, index) => (
                <Link
                  key={index}
                  to={item.url}
                  className="block bg-white p-6 rounded-lg border border-gray-100 hover:border-[#866BFF] transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.name}</h3>
                  {item.description && (
                    <p className="text-gray-600 mb-3">{item.description}</p>
                  )}
                  <div className="text-[#866BFF]">{t('categoryPage.explore')} →</div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <Footer />
      </div>
    </div>
  )
}

export default CategoryPage 